import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Truck, 
  RefreshCw,
  Loader2,
  Wrench,
  MessageSquare,
  CheckCircle,
  Clock,
  AlertCircle,
  Search,
  ChevronRight,
  ExternalLink
} from 'lucide-react';
import { Input } from '../components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fleetio account slug for direct links
const FLEETIO_ACCOUNT_SLUG = '774f6bae37';

// Helper function for relative time
function getTimeSince(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

// Color mapping for statuses - using darker text for better contrast
function getStatusColor(color) {
  const colorMap = {
    green: 'bg-green-500/20 text-green-300 border-green-500/30',
    red: 'bg-red-500/20 text-red-300 border-red-500/30',
    orange: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
    blue: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    gray: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
    teal: 'bg-teal-500/20 text-teal-300 border-teal-500/30',
    olive: 'bg-lime-500/20 text-lime-300 border-lime-500/30'
  };
  return colorMap[color] || colorMap.gray;
}

export default function FleetioPage() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('vehicles');
  
  // Data states
  const [vehicleData, setVehicleData] = useState({ total: 0, summary: [], by_status: {} });
  const [vehicles, setVehicles] = useState([]);
  const [workOrderData, setWorkOrderData] = useState({ total: 0, summary: [], by_status: {} });
  const [workOrders, setWorkOrders] = useState([]);
  const [comments, setComments] = useState([]);
  
  // Filters
  const [vehicleStatusFilter, setVehicleStatusFilter] = useState(null);
  const [workOrderStatusFilter, setWorkOrderStatusFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingWorkOrders, setLoadingWorkOrders] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  // Fetch work orders when filter changes
  useEffect(() => {
    if (workOrderStatusFilter !== null) {
      fetchWorkOrdersByStatus(workOrderStatusFilter);
    }
  }, [workOrderStatusFilter]);

  const fetchWorkOrdersByStatus = async (status) => {
    setLoadingWorkOrders(true);
    const token = localStorage.getItem('vaultai_token');
    if (!token) return;
    
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      const url = status 
        ? `${API}/fleetio/work-orders?status=${status.toLowerCase()}&limit=100`
        : `${API}/fleetio/work-orders?limit=100`;
      
      const response = await axios.get(url, { headers });
      
      if (response.data.success) {
        setWorkOrders(response.data.work_orders || []);
      }
    } catch (err) {
      console.error('Fleetio work orders fetch error:', err);
    } finally {
      setLoadingWorkOrders(false);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    
    const token = localStorage.getItem('vaultai_token');
    if (!token) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }
    
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      // Fetch all data in parallel
      const [dashboardRes, vehiclesRes, workOrdersRes, commentsRes] = await Promise.all([
        axios.get(`${API}/fleetio/dashboard`, { headers }).catch(e => ({ data: { success: false } })),
        axios.get(`${API}/fleetio/vehicles`, { headers }).catch(e => ({ data: { success: false, vehicles: [] } })),
        axios.get(`${API}/fleetio/work-orders?limit=100`, { headers }).catch(e => ({ data: { success: false, work_orders: [] } })),
        axios.get(`${API}/fleetio/comments?limit=50`, { headers }).catch(e => ({ data: { success: false, comments: [] } }))
      ]);
      
      if (dashboardRes.data.success) {
        setVehicleData(dashboardRes.data.vehicles);
        setWorkOrderData(dashboardRes.data.work_orders);
      }
      
      if (vehiclesRes.data.success) {
        setVehicles(vehiclesRes.data.vehicles || []);
      }
      
      if (workOrdersRes.data.success) {
        setWorkOrders(workOrdersRes.data.work_orders || []);
      }
      
      if (commentsRes.data.success) {
        setComments(commentsRes.data.comments || []);
      }
      
    } catch (err) {
      console.error('Fleetio fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAllData();
  };

  // Filter vehicles
  const filteredVehicles = vehicles.filter(v => {
    if (vehicleStatusFilter && v.status !== vehicleStatusFilter) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (v.name?.toLowerCase().includes(search) || 
              v.vin?.toLowerCase().includes(search) ||
              v.license_plate?.toLowerCase().includes(search) ||
              v.make?.toLowerCase().includes(search) ||
              v.model?.toLowerCase().includes(search));
    }
    return true;
  });

  // Filter work orders - now server-side filtered, just apply search
  const filteredWorkOrders = workOrders.filter(wo => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (wo.number?.toLowerCase().includes(search) ||
              wo.description?.toLowerCase().includes(search) ||
              wo.vehicle_name?.toLowerCase().includes(search));
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="fleetio-loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-lg">Loading Fleetio data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="fleetio-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Fleetio</h1>
          <p className="text-muted-foreground text-sm">Vehicle status, work orders & activity</p>
        </div>
        <Button 
          onClick={handleRefresh} 
          variant="outline" 
          disabled={refreshing}
          data-testid="fleetio-refresh-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="h-4 w-4 inline mr-2" />
          {error}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Vehicle Status Summary */}
        <Card data-testid="vehicle-status-summary">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Truck className="h-5 w-5 text-blue-600" />
              Vehicle Status
              <Badge variant="outline" className="ml-auto">{vehicleData.total} total</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {vehicleData.summary?.map((s, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setVehicleStatusFilter(vehicleStatusFilter === s.status ? null : s.status);
                    setActiveTab('vehicles');
                  }}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                    getStatusColor(s.color)
                  } ${vehicleStatusFilter === s.status ? 'ring-2 ring-offset-1 ring-blue-500' : 'hover:scale-105'}`}
                >
                  {s.status}: {s.count}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Work Order Summary */}
        <Card data-testid="work-order-summary">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Wrench className="h-5 w-5 text-orange-600" />
              Work Orders
              <Badge variant="outline" className="ml-auto">{workOrderData.total} recent</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {workOrderData.summary?.map((s, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setWorkOrderStatusFilter(workOrderStatusFilter === s.status ? null : s.status);
                    setActiveTab('workorders');
                  }}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                    getStatusColor(s.color)
                  } ${workOrderStatusFilter === s.status ? 'ring-2 ring-offset-1 ring-blue-500' : 'hover:scale-105'}`}
                >
                  {s.status}: {s.count}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="vehicles" data-testid="tab-vehicles">
            <Truck className="h-4 w-4 mr-2" />
            Vehicles ({filteredVehicles.length})
          </TabsTrigger>
          <TabsTrigger value="workorders" data-testid="tab-workorders">
            <Wrench className="h-4 w-4 mr-2" />
            Work Orders ({workOrderData.total || filteredWorkOrders.length})
          </TabsTrigger>
          <TabsTrigger value="comments" data-testid="tab-comments">
            <MessageSquare className="h-4 w-4 mr-2" />
            Comments ({comments.length})
          </TabsTrigger>
        </TabsList>

        {/* Search Bar */}
        <div className="mt-4 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search vehicles, work orders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="fleetio-search"
          />
        </div>

        {/* Vehicles Tab */}
        <TabsContent value="vehicles" className="mt-4">
          {vehicleStatusFilter && (
            <div className="mb-4 flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Filtered by:</span>
              <Badge variant="secondary" className="cursor-pointer" onClick={() => setVehicleStatusFilter(null)}>
                {vehicleStatusFilter} ✕
              </Badge>
            </div>
          )}
          
          <div className="grid gap-3">
            {filteredVehicles.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No vehicles found
              </div>
            ) : (
              <TooltipProvider>
              {filteredVehicles.map((vehicle, idx) => (
                <Card 
                  key={idx} 
                  className="hover:shadow-md transition-shadow cursor-pointer group" 
                  data-testid={`vehicle-card-${idx}`}
                  onClick={() => window.open(`https://secure.fleetio.com/${FLEETIO_ACCOUNT_SLUG}/vehicles/${vehicle.id}`, '_blank')}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-50">
                          <Truck className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold">{vehicle.name}</h3>
                          <p className="text-sm text-foreground/70">
                            {vehicle.year} {vehicle.make} {vehicle.model}
                            {vehicle.license_plate && ` • ${vehicle.license_plate}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={getStatusColor(vehicle.status_color)}>
                          {vehicle.status}
                        </Badge>
                        {vehicle.work_orders_count > 0 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="outline" className="text-orange-600 border-orange-200">
                                <Wrench className="h-3 w-3 mr-1" />
                                {vehicle.work_orders_count}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{vehicle.work_orders_count} Work Order{vehicle.work_orders_count !== 1 ? 's' : ''}</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {vehicle.issues_count > 0 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="outline" className="text-red-600 border-red-200">
                                <AlertCircle className="h-3 w-3 mr-1" />
                                {vehicle.issues_count}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{vehicle.issues_count} Open Issue{vehicle.issues_count !== 1 ? 's' : ''}</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center text-muted-foreground group-hover:text-blue-400 transition-colors">
                              <ExternalLink className="h-4 w-4" />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Open in Fleetio</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>
                    {(vehicle.primary_meter || vehicle.group || vehicle.vehicle_type) && (
                      <div className="mt-2 pt-2 border-t flex gap-4 text-xs text-foreground/70">
                        {vehicle.primary_meter && (
                          <span>{vehicle.primary_meter?.toLocaleString()} {vehicle.primary_meter_unit}</span>
                        )}
                        {vehicle.vehicle_type && <span>Type: {vehicle.vehicle_type}</span>}
                        {vehicle.group && <span>Group: {vehicle.group}</span>}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              </TooltipProvider>
            )}
          </div>
        </TabsContent>

        {/* Work Orders Tab */}
        <TabsContent value="workorders" className="mt-4">
          {workOrderStatusFilter && (
            <div className="mb-4 flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Filtered by:</span>
              <Badge variant="secondary" className="cursor-pointer" onClick={() => {
                setWorkOrderStatusFilter(null);
                fetchWorkOrdersByStatus(null);
              }}>
                {workOrderStatusFilter} ✕
              </Badge>
            </div>
          )}
          
          <div className="grid gap-3">
            {loadingWorkOrders ? (
              <div className="text-center py-8">
                <Loader2 className="h-6 w-6 animate-spin mx-auto text-blue-600" />
                <span className="text-sm text-muted-foreground mt-2 block">Loading work orders...</span>
              </div>
            ) : filteredWorkOrders.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No work orders found
              </div>
            ) : (
              <TooltipProvider>
              {filteredWorkOrders.map((wo, idx) => (
                <Card 
                  key={idx} 
                  className="hover:shadow-md transition-shadow cursor-pointer group" 
                  data-testid={`work-order-card-${idx}`}
                  onClick={() => window.open(`https://secure.fleetio.com/${FLEETIO_ACCOUNT_SLUG}/vehicles/${wo.vehicle_id}/work_orders`, '_blank')}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className={`p-2 rounded-lg ${
                              wo.state === 'completed' ? 'bg-green-50' : 
                              wo.state === 'open' ? 'bg-teal-50' : 'bg-orange-50'
                            }`}>
                              {wo.state === 'completed' ? (
                                <CheckCircle className="h-5 w-5 text-green-600" />
                              ) : wo.state === 'open' ? (
                                <Wrench className="h-5 w-5 text-teal-600" />
                              ) : (
                                <Clock className="h-5 w-5 text-orange-600" />
                              )}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{wo.state === 'completed' ? 'Completed' : wo.state === 'open' ? 'Open' : 'In Progress'}</p>
                          </TooltipContent>
                        </Tooltip>
                        <div>
                          <h3 className="font-semibold">{wo.number}</h3>
                          <p className="text-sm text-foreground/70 line-clamp-1">
                            {wo.description || 'No description'}
                          </p>
                          {wo.vehicle_name && (
                            <p className="text-xs text-blue-600 mt-1">
                              <Truck className="h-3 w-3 inline mr-1" />
                              {wo.vehicle_name}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <div className="flex items-center gap-2">
                          <Badge className={getStatusColor(wo.status_color)}>
                            {wo.status}
                          </Badge>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="text-muted-foreground group-hover:text-blue-400 transition-colors">
                                <ExternalLink className="h-4 w-4" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Open in Fleetio</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <span className="text-xs text-foreground/70">
                          {wo.completed_at ? `Completed ${getTimeSince(wo.completed_at)}` : 
                           wo.started_at ? `Started ${getTimeSince(wo.started_at)}` : 
                           `Created ${getTimeSince(wo.created_at)}`}
                        </span>
                        {wo.total_amount > 0 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-xs font-medium text-green-600">
                                ${wo.total_amount?.toFixed(2)}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Total Cost</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                    {wo.issues?.length > 0 && (
                      <div className="mt-2 pt-2 border-t">
                        <div className="flex flex-wrap gap-1">
                          {wo.issues.map((issue, i) => (
                            <Tooltip key={i}>
                              <TooltipTrigger asChild>
                                <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                                  {issue}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Related Issue</p>
                              </TooltipContent>
                            </Tooltip>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              </TooltipProvider>
            )}
          </div>
        </TabsContent>

        {/* Comments Tab */}
        <TabsContent value="comments" className="mt-4">
          <div className="grid gap-3">
            {comments.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No comments found
              </div>
            ) : (
              <TooltipProvider>
              {comments.map((comment, idx) => (
                <Card 
                  key={idx} 
                  className={`hover:shadow-md transition-shadow ${comment.vehicle_id ? 'cursor-pointer group' : ''}`}
                  data-testid={`comment-card-${idx}`}
                  onClick={() => comment.vehicle_id && window.open(`https://secure.fleetio.com/${FLEETIO_ACCOUNT_SLUG}/vehicles/${comment.vehicle_id}/work_orders`, '_blank')}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-full bg-secondary">
                        <MessageSquare className="h-4 w-4 text-foreground" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm">{comment.user_name}</span>
                            <Badge variant="outline" className="text-xs">
                              {comment.commentable_type}
                            </Badge>
                            {comment.work_order_number && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="outline" className="text-xs bg-orange-500/20 text-orange-300 border-orange-500/30">
                                    <Wrench className="h-3 w-3 mr-1" />
                                    WO #{comment.work_order_number}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Work Order #{comment.work_order_number}</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            {comment.vehicle_name && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="outline" className="text-xs bg-blue-500/20 text-blue-300 border-blue-500/30">
                                    <Truck className="h-3 w-3 mr-1" />
                                    {comment.vehicle_name}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Vehicle: {comment.vehicle_name}</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-foreground/70">{getTimeSince(comment.created_at)}</span>
                            {comment.vehicle_id && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="text-muted-foreground group-hover:text-blue-400 transition-colors">
                                    <ExternalLink className="h-4 w-4" />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Open in Fleetio</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-foreground mt-2">{comment.comment}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              </TooltipProvider>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
